const API_BASE = "";
const STORAGE_KEY = "analytics_session_id";
const PING_INTERVAL_MS = 15000;
const EVENT_FLUSH_INTERVAL_MS = 5000;
const MOUSEMOVE_THROTTLE_MS = 450;
const SCROLL_REPLAY_THROTTLE_MS = 900;
const MAX_EVENT_QUEUE_SIZE = 600;
const MAX_REPLAY_QUEUE_SIZE = 1600;
const SCROLL_MILESTONES = [25, 50, 75, 90, 100];

let activeSessionId = null;
let eventQueue = [];
let replayQueue = [];
let flushTimerId = null;
let isFlushingEvents = false;

function getCurrentPath() {
  return `${window.location.pathname}${window.location.search}`;
}

function isAdminPath(pathname = window.location.pathname) {
  return pathname.startsWith("/admin");
}

function isAnalyticsPreview() {
  return new URLSearchParams(window.location.search).get("analytics_preview") === "1";
}

function parsePathAndQuery() {
  return {
    path: window.location.pathname,
    query_string: window.location.search || ""
  };
}

function getStoredSessionId() {
  if (typeof window === "undefined") return null;
  return activeSessionId || window.sessionStorage.getItem(STORAGE_KEY);
}

function clipText(value, maxLength = 500) {
  if (value === undefined || value === null) return "";
  return String(value).replace(/\s+/g, " ").trim().slice(0, maxLength);
}

function getViewportMeta() {
  return {
    viewport_width: window.innerWidth || 0,
    viewport_height: window.innerHeight || 0
  };
}

function getScrollPct() {
  const doc = document.documentElement;
  const scrollable = Math.max(1, doc.scrollHeight - window.innerHeight);
  return Math.max(0, Math.min(100, (window.scrollY / scrollable) * 100));
}

function getPointPercent(event) {
  if (!event) return {};
  const width = window.innerWidth || 1;
  const height = window.innerHeight || 1;
  return {
    x_pct: Math.max(0, Math.min(100, (event.clientX / width) * 100)),
    y_pct: Math.max(0, Math.min(100, (event.clientY / height) * 100))
  };
}

function shouldTrack() {
  return typeof window !== "undefined" && !isAdminPath() && !isAnalyticsPreview();
}

function normalizeMetadata(metadata = {}) {
  const allowedKeys = [
    "tag",
    "id",
    "classes",
    "text",
    "aria_label",
    "role",
    "section",
    "button_text",
    "is_outbound",
    "click_count",
    "duration_ms",
    "scroll_y"
  ];
  return allowedKeys.reduce((acc, key) => {
    if (metadata[key] === undefined || metadata[key] === null) return acc;
    acc[key] =
      typeof metadata[key] === "number" || typeof metadata[key] === "boolean"
        ? metadata[key]
        : clipText(metadata[key], 260);
    return acc;
  }, {});
}

function trimQueues() {
  if (eventQueue.length > MAX_EVENT_QUEUE_SIZE) {
    eventQueue = eventQueue.slice(-MAX_EVENT_QUEUE_SIZE);
  }
  if (replayQueue.length > MAX_REPLAY_QUEUE_SIZE) {
    replayQueue = replayQueue.slice(-MAX_REPLAY_QUEUE_SIZE);
  }
}

async function postJson(path, payload) {
  const response = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: {"Content-Type": "application/json"},
    body: JSON.stringify(payload),
    keepalive: true
  });
  if (!response.ok) {
    throw new Error(`Request failed: ${response.status}`);
  }
  return response.json();
}

function buildAnalyticsPayload(eventType, options = {}) {
  const sessionId = getStoredSessionId();
  if (!sessionId) return null;
  return {
    session_id: sessionId,
    event_type: clipText(eventType, 80),
    label: clipText(options.label),
    target: clipText(options.target, 900),
    path: clipText(options.path || getCurrentPath(), 900) || "/",
    x_pct: options.x_pct,
    y_pct: options.y_pct,
    scroll_pct:
      options.scroll_pct === undefined ? getScrollPct() : options.scroll_pct,
    ...getViewportMeta(),
    metadata: normalizeMetadata(options.metadata || {}),
    client_created_at: new Date().toISOString()
  };
}

function enqueueAnalyticsEvent(eventType, options = {}) {
  if (!shouldTrack()) return;
  const payload = buildAnalyticsPayload(eventType, options);
  if (!payload || !payload.event_type) return;
  eventQueue.push(payload);
  trimQueues();
}

function enqueueReplayEvent(eventType, options = {}) {
  if (!shouldTrack()) return;
  const payload = buildAnalyticsPayload(eventType, options);
  if (!payload || !payload.event_type) return;
  replayQueue.push(payload);
  trimQueues();
}

function sendEventsBeacon() {
  if (!eventQueue.length && !replayQueue.length) return;
  const payload = JSON.stringify({
    events: eventQueue.splice(0, eventQueue.length),
    replay_events: replayQueue.splice(0, replayQueue.length)
  });
  const url = `${API_BASE}/api/events/batch`;
  if (navigator.sendBeacon) {
    navigator.sendBeacon(url, new Blob([payload], {type: "application/json"}));
    return;
  }
  fetch(url, {
    method: "POST",
    headers: {"Content-Type": "application/json"},
    body: payload,
    keepalive: true
  }).catch(() => {});
}

async function flushAnalyticsEvents() {
  if (isFlushingEvents || (!eventQueue.length && !replayQueue.length)) return;
  isFlushingEvents = true;
  const events = eventQueue.splice(0, eventQueue.length);
  const replayEvents = replayQueue.splice(0, replayQueue.length);
  try {
    await postJson("/api/events/batch", {
      events,
      replay_events: replayEvents
    });
  } catch (_) {
    eventQueue = events.concat(eventQueue).slice(-MAX_EVENT_QUEUE_SIZE);
    replayQueue = replayEvents.concat(replayQueue).slice(-MAX_REPLAY_QUEUE_SIZE);
  } finally {
    isFlushingEvents = false;
  }
}

function sendEndBeacon(sessionId) {
  const url = `${API_BASE}/api/visit/end`;
  const payload = JSON.stringify({session_id: sessionId});
  if (navigator.sendBeacon) {
    const blob = new Blob([payload], {type: "application/json"});
    navigator.sendBeacon(url, blob);
    return;
  }

  fetch(url, {
    method: "POST",
    headers: {"Content-Type": "application/json"},
    body: payload,
    keepalive: true
  }).catch(() => {});
}

function describeClickable(element) {
  const clickable = element?.closest?.(
    "[data-analytics-label], a, button, [role='button'], input[type='button'], input[type='submit']"
  );
  if (!clickable) return null;
  const href = clickable.getAttribute("href") || "";
  const tag = clickable.tagName?.toLowerCase() || "";
  const explicitLabel = clickable.getAttribute("data-analytics-label");
  const ariaLabel = clickable.getAttribute("aria-label");
  const title = clickable.getAttribute("title");
  const text = clipText(clickable.innerText || clickable.textContent || "", 140);
  const label =
    explicitLabel ||
    ariaLabel ||
    title ||
    text ||
    href ||
    clickable.getAttribute("id") ||
    tag;
  const target = href || clickable.getAttribute("data-analytics-target") || "";
  const classes =
    typeof clickable.className === "string" ? clickable.className : "";
  return {
    label,
    target,
    metadata: {
      tag,
      id: clickable.getAttribute("id") || "",
      classes,
      text,
      aria_label: ariaLabel || "",
      role: clickable.getAttribute("role") || "",
      is_outbound: Boolean(
        href && /^https?:\/\//i.test(href) && !href.includes(window.location.host)
      )
    }
  };
}

export function trackAnalyticsEvent(eventType, options = {}) {
  enqueueAnalyticsEvent(eventType, options);
}

export function initVisitorTracking() {
  if (typeof window === "undefined") {
    return () => {};
  }
  if (isAdminPath()) {
    return () => {};
  }
  if (isAnalyticsPreview()) {
    return () => {};
  }

  let sessionId = window.sessionStorage.getItem(STORAGE_KEY);
  let intervalId = null;
  let routeCheckIntervalId = null;
  let lastMouseMoveAt = 0;
  let lastScrollReplayAt = 0;
  let highestScrollMilestone = 0;
  let recentClicks = [];
  let sectionObserver = null;
  const visibleSections = new Map();
  const seenSections = new Set();
  let isEnding = false;
  let previousPath = getCurrentPath();

  const sendPing = async () => {
    if (!sessionId || isEnding) return;
    if (isAdminPath()) return;
    try {
      await postJson("/api/visit/ping", {
        session_id: sessionId,
        ...parsePathAndQuery()
      });
    } catch (_) {
      // Do not throw in UI for analytics transport failures.
    }
  };

  const onVisibilityChange = () => {
    enqueueAnalyticsEvent("visibility", {
      label: document.visibilityState,
      metadata: {scroll_y: Math.round(window.scrollY)}
    });
    if (document.visibilityState === "hidden" && sessionId) {
      sendEventsBeacon();
      sendEndBeacon(sessionId);
    }
  };

  const onBeforeUnload = () => {
    sendEventsBeacon();
    if (sessionId) {
      sendEndBeacon(sessionId);
    }
  };

  const onRouteCheck = () => {
    const currentPath = getCurrentPath();
    if (currentPath !== previousPath) {
      previousPath = currentPath;
      enqueueAnalyticsEvent("route_change", {label: currentPath});
      enqueueReplayEvent("route_change", {label: currentPath});
      sendPing();
    }
  };

  const onDocumentClick = event => {
    const clickable = describeClickable(event.target);
    const point = getPointPercent(event);
    enqueueReplayEvent("click", {
      ...point,
      target: clickable?.target || "",
      label: clickable?.label || "Document click",
      metadata: clickable?.metadata || {}
    });
    if (!clickable) return;
    enqueueAnalyticsEvent("click", {
      ...point,
      label: clickable.label,
      target: clickable.target,
      metadata: clickable.metadata
    });

    const now = Date.now();
    recentClicks = recentClicks
      .filter(item => now - item.timestamp < 950)
      .concat([{timestamp: now, x: event.clientX, y: event.clientY}]);
    const closeClicks = recentClicks.filter(
      item =>
        Math.abs(item.x - event.clientX) < 42 &&
        Math.abs(item.y - event.clientY) < 42
    );
    if (closeClicks.length >= 3) {
      enqueueAnalyticsEvent("rage_click", {
        ...point,
        label: clickable.label,
        target: clickable.target,
        metadata: {...clickable.metadata, click_count: closeClicks.length}
      });
      recentClicks = [];
    }
  };

  const onMouseMove = event => {
    const now = Date.now();
    if (now - lastMouseMoveAt < MOUSEMOVE_THROTTLE_MS) return;
    lastMouseMoveAt = now;
    enqueueReplayEvent("mousemove", {
      ...getPointPercent(event),
      metadata: {scroll_y: Math.round(window.scrollY)}
    });
  };

  const onScroll = () => {
    const now = Date.now();
    const scrollPct = getScrollPct();
    const nextMilestone = SCROLL_MILESTONES.find(
      milestone => scrollPct >= milestone && milestone > highestScrollMilestone
    );
    if (nextMilestone) {
      highestScrollMilestone = nextMilestone;
      enqueueAnalyticsEvent("scroll_depth", {
        label: `${nextMilestone}%`,
        scroll_pct: nextMilestone,
        metadata: {scroll_y: Math.round(window.scrollY)}
      });
    }
    if (now - lastScrollReplayAt >= SCROLL_REPLAY_THROTTLE_MS) {
      lastScrollReplayAt = now;
      enqueueReplayEvent("scroll", {
        scroll_pct: scrollPct,
        metadata: {scroll_y: Math.round(window.scrollY)}
      });
    }
  };

  const startSectionTracking = () => {
    if (!("IntersectionObserver" in window)) return;
    const sections = Array.from(
      document.querySelectorAll("section[id], div[id]")
    ).filter(section => {
      const id = section.getAttribute("id");
      return id && !["root", "resume"].includes(id);
    });
    sectionObserver = new IntersectionObserver(
      entries => {
        const now = Date.now();
        entries.forEach(entry => {
          const section = entry.target.getAttribute("id");
          if (!section) return;
          if (entry.isIntersecting && entry.intersectionRatio >= 0.45) {
            visibleSections.set(section, now);
            if (!seenSections.has(section)) {
              seenSections.add(section);
              enqueueAnalyticsEvent("section_view", {
                label: section,
                metadata: {section}
              });
            }
          } else if (visibleSections.has(section)) {
            const startedAt = visibleSections.get(section);
            visibleSections.delete(section);
            const durationMs = Math.max(0, now - startedAt);
            if (durationMs > 1200) {
              enqueueAnalyticsEvent("section_time", {
                label: section,
                metadata: {section, duration_ms: durationMs}
              });
            }
          }
        });
      },
      {threshold: [0, 0.45, 0.75]}
    );
    sections.forEach(section => sectionObserver.observe(section));
  };

  const start = async () => {
    try {
      if (!sessionId) {
        const data = await postJson("/api/visit/start", parsePathAndQuery());
        sessionId = data.session_id;
        activeSessionId = sessionId;
        if (sessionId) {
          window.sessionStorage.setItem(STORAGE_KEY, sessionId);
        }
      } else {
        activeSessionId = sessionId;
        await sendPing();
      }

      intervalId = window.setInterval(sendPing, PING_INTERVAL_MS);
      routeCheckIntervalId = window.setInterval(onRouteCheck, 1500);
      flushTimerId = window.setInterval(
        flushAnalyticsEvents,
        EVENT_FLUSH_INTERVAL_MS
      );
      enqueueAnalyticsEvent("page_view", {label: getCurrentPath()});
      enqueueReplayEvent("page_view", {label: getCurrentPath()});
      document.addEventListener("visibilitychange", onVisibilityChange);
      document.addEventListener("click", onDocumentClick, true);
      document.addEventListener("mousemove", onMouseMove, {passive: true});
      document.addEventListener("scroll", onScroll, {passive: true});
      window.addEventListener("beforeunload", onBeforeUnload);
      window.setTimeout(startSectionTracking, 800);
    } catch (_) {
      // Keep app running even if analytics backend is unavailable.
    }
  };

  start();

  return () => {
    isEnding = true;
    if (intervalId) {
      window.clearInterval(intervalId);
    }
    if (routeCheckIntervalId) {
      window.clearInterval(routeCheckIntervalId);
    }
    if (flushTimerId) {
      window.clearInterval(flushTimerId);
    }
    if (sectionObserver) {
      sectionObserver.disconnect();
    }
    document.removeEventListener("visibilitychange", onVisibilityChange);
    document.removeEventListener("click", onDocumentClick, true);
    document.removeEventListener("mousemove", onMouseMove);
    document.removeEventListener("scroll", onScroll);
    window.removeEventListener("beforeunload", onBeforeUnload);
    sendEventsBeacon();
    if (sessionId) {
      sendEndBeacon(sessionId);
    }
  };
}
